import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import IconPicker, { resolveIcon } from "./IconPicker";
import ColorPicker from "./ColorPicker";
import api from "../lib/api";
import { Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function DeckEditor({ open, onOpenChange, deck, onSaved }) {
  const isEdit = !!deck;
  const [form, setForm] = useState({
    name: "",
    icon: "Stack",
    color: "#2563EB",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (deck) {
        setForm({
          name: deck.name || "",
          icon: deck.icon || "Stack",
          color: deck.color || "#2563EB",
        });
      } else {
        setForm({ name: "", icon: "Stack", color: "#2563EB" });
      }
    }
  }, [open, deck]);

  const Icon = resolveIcon(form.icon);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Pon un nombre a la plataforma");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.put(`/decks/${deck.deck_id}`, form);
        onSaved(data, "update");
      } else {
        const { data } = await api.post("/decks", form);
        onSaved(data, "create");
      }
      onOpenChange(false);
    } catch (e) {
      toast.error("No se pudo guardar la plataforma");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm("¿Eliminar esta plataforma y todos sus botones?")) return;
    try {
      await api.delete(`/decks/${deck.deck_id}`);
      onSaved(deck.deck_id, "delete");
      onOpenChange(false);
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-deck-sidebar border-white/10 text-white max-w-md"
        data-testid="deck-editor"
      >
        <DialogHeader>
          <DialogTitle className="text-white font-heading text-2xl">
            {isEdit ? "Editar plataforma" : "Nueva plataforma"}
          </DialogTitle>
          <DialogDescription className="text-white/50 font-mono text-xs uppercase tracking-[0.15em]">
            {isEdit ? "Cambia el nombre o ícono" : "Crea un grupo para tus botones"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center my-4">
          <div
            data-testid="deck-preview"
            className="deck-tile w-20 h-20 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: form.color }}
          >
            <Icon size={36} weight="fill" className="text-white" />
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">
              Nombre
            </Label>
            <Input
              data-testid="deck-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Trabajo, Personal, Gaming..."
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
          </div>

          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">
              Color
            </Label>
            <ColorPicker
              value={form.color}
              textColor="#FFFFFF"
              onChange={({ color }) => setForm({ ...form, color })}
            />
          </div>

          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">
              Ícono
            </Label>
            <IconPicker value={form.icon} onChange={(icon) => setForm({ ...form, icon })} />
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            data-testid="deck-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#2563EB] text-white hover:bg-[#1D4ED8] font-heading font-bold"
          >
            {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear plataforma"}
          </Button>
          {isEdit && (
            <Button
              data-testid="deck-delete-btn"
              variant="outline"
              onClick={handleDelete}
              className="w-full bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              <Trash size={16} className="mr-2" /> Eliminar plataforma
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
